import React from 'react';
import { Button, ButtonAppearance, Icon, useDeviceSize } from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

import { getOptions } from 'src/pages/LessonPage/children/utils/getOptions';
import { ImageContentBlock } from 'src/graphql/generated/graphql';

import SectionHeading from './SectionHeading';

const ImageContentBlockSection: React.FC<{
  contentBlock: ImageContentBlock;
}> = ({ contentBlock }) => {
  const deviceWidth = useDeviceSize();
  const inspectorProps = useContentfulInspectorMode();

  const title = contentBlock.titleExternal;
  const eyebrow = contentBlock.eyebrow;
  const imageUrl = contentBlock.asset?.cdnUrl ?? contentBlock.asset?.image?.url;
  const imageAlt = contentBlock.asset?.altText;
  const contentText = contentBlock.contentText?.json;
  const cta = contentBlock.cta;

  const handleCtaClick: VoidFunction = () => {
    if (cta?.link) {
      window.location.href = cta.link;
    }
  };

  return (
    <div css={outerContainerStyles}>
      <SectionHeading heading={title} eyebrow={eyebrow} />
      <div css={contentContainerStyles}>
        <div css={imageContainerStyles}>
          <img src={imageUrl} alt={imageAlt} />
        </div>
        <div css={contentTextStyles}>
          <div css={richTextStyles} {...inspectorProps({ entryId: contentBlock.sys.id, fieldId: 'children' })}>
            {contentText && documentToReactComponents(contentText, getOptions(contentText, deviceWidth))}
          </div>
          <Button
            appearance={ButtonAppearance.PRIMARY}
            onClick={handleCtaClick}
            target={contentBlock.cta?.target}
            label={contentBlock.cta?.label}
            iconAfter={<Icon type="arrow-right" />}
          />
        </div>
      </div>
    </div>
  );
};

const outerContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.500'),
});

const contentContainerStyles = css({
  display: 'grid',
  gap: token('space.500'),
  gridTemplateColumns: '1fr 1fr',

  '& > div': {
    maxWidth: '424px',
  },

  [media.below.md]: {
    gridTemplateColumns: '1fr',
  },
});

const richTextStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.300'),

  '& div#paragraph-block': {
    paddingBottom: 0,
  },
});

const contentTextStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.300'),

  [media.below.md]: {
    maxWidth: '390px',
    margin: '0 auto',
  },
});

const imageContainerStyles = css({
  marginLeft: 'auto',

  [media.below.md]: {
    margin: '0 auto',
    maxWidth: '390px',

    '& img': {
      width: '100%',
    },
  },
});

export default ImageContentBlockSection;
